/**
 * AI CONTEXT
 * Purpose: the Teacher Stats table's expandable row — a bar histogram of
 *   moments-per-student for one teacher's current-semester roster (bucket 0
 *   is roster students the teacher has never written a moment for, the
 *   "unevenness" signal the owner asked for). Plain divs, no charting
 *   library (hard rule: no new dependencies) — a flex row of fixed-width
 *   bars, height scaled to the largest bucket, each wrapped in a Tooltip
 *   listing that bucket's student names.
 * Used by: StandoutTeacherStats (Table `expandable.expandedRowRender`).
 */
import { Tooltip, Typography } from "antd"
import { keyBy } from "lodash"
import { useMemo } from "react"

import { studentAtoms } from "#atoms"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { useSomeDocs } from "#hooks/jotai/useSubscription"

import {
  buildTeacherStudentDistribution,
  getTeacherRosterStudentIds,
  type StandoutDistributionBucket,
} from "./standoutReviewHelpers"

const { Text } = Typography

const BAR_WIDTH = 22
const BAR_MAX_HEIGHT = 100
const BAR_MIN_HEIGHT = 2
const MAX_TOOLTIP_NAMES = 15

interface StandoutDistributionChartProps {
  teacherId: Uuid
  moments: readonly StandoutMomentConstructor[]
  semesterClasses: readonly ClassConstructor[]
  studentsById: Record<Uuid, StudentConstructor>
}

const bucketTooltip = (bucket: StandoutDistributionBucket) => {
  if (bucket.students.length === 0) return "No students"
  const names = bucket.students.map((student) => student.name)
  const shown = names.slice(0, MAX_TOOLTIP_NAMES)
  const extra = names.length - shown.length
  return (
    <div>
      {shown.map((name) => (
        <div key={name}>{name}</div>
      ))}
      {extra > 0 ? <div>… and {extra} more</div> : null}
    </div>
  )
}

const StandoutDistributionChart = ({
  teacherId,
  moments,
  semesterClasses,
  studentsById,
}: StandoutDistributionChartProps) => {
  const theme = useThemeContext()

  // Bucket-0 names are the chart's whole point ("which kids has this teacher
  // never written about"), and those students have no moments, so the view's
  // moments-derived student subscription never loads them. Subscribe to THIS
  // teacher's roster here instead: the chart only mounts when its row is
  // expanded, so the read stays bounded to one teacher's class sizes.
  const rosterIds = useMemo(
    () => getTeacherRosterStudentIds(teacherId, semesterClasses).sort(),
    [semesterClasses, teacherId]
  )
  const rosterStudents = useSomeDocs(
    studentAtoms,
    DOC_TYPES.STUDENTS,
    rosterIds
  )
  const mergedStudentsById = useMemo(
    () => ({ ...studentsById, ...keyBy(rosterStudents, "id") }),
    [rosterStudents, studentsById]
  )

  const buckets = useMemo(
    () =>
      buildTeacherStudentDistribution(
        teacherId,
        moments,
        semesterClasses,
        mergedStudentsById
      ),
    [moments, semesterClasses, mergedStudentsById, teacherId]
  )

  const maxCount = Math.max(
    1,
    ...buckets.map((bucket) => bucket.students.length)
  )

  return (
    <div
      data-testid="sr-distribution"
      data-teacher-id={teacherId}
      style={{
        display: "flex",
        gap: 14,
        alignItems: "flex-end",
        padding: "12px 8px",
      }}
    >
      {buckets.map((bucket) => {
        const count = bucket.students.length
        const height =
          count === 0
            ? BAR_MIN_HEIGHT
            : Math.max(
                BAR_MIN_HEIGHT,
                Math.round((count / maxCount) * BAR_MAX_HEIGHT)
              )
        return (
          <Tooltip key={bucket.bucket} title={bucketTooltip(bucket)}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
              }}
            >
              <div
                data-testid="sr-distribution-bar"
                data-bucket={bucket.bucket}
                data-count={count}
                style={{
                  width: BAR_WIDTH,
                  height,
                  borderRadius: 2,
                  backgroundColor: theme.eric.blue,
                }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {bucket.bucket}
              </Text>
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}

export default StandoutDistributionChart
